import { Cell, Grid, Theme, VFlow } from 'bold-ui'
import { useScrollPosition } from 'bold-ui/lib/hooks'
import CheckPermission from 'components/auth/CheckPermission'
import { useCabecalhoMinimizado } from 'hooks/useCabecalhoMinimizado'
import React, { CSSProperties, useMemo } from 'react'
import { useLocalStyles } from 'view/atendimentos/atendimento-individual/AtendimentoIndividualView'

import { LembreteFormModel } from '../../components/modals/lembretes/components/LembreteForm'
import { AlergiaReacaoModel } from '../avaliacao/components/alergias-reacoes/model'
import { ProblemaCondicaoModel } from '../avaliacao/components/problemas-condicoes/model'
import { grupoCboLembretes } from '../plano/acessos'
import { AlergiasSection } from './alergias/AlergiasSection'
import { CondicoesAutorreferidasSection } from './condicoes-autorreferidas/CondicoesAutorreferidasSection'
import { LembretesSection } from './lembretes/LembretesSection'
import { ProblemasSection } from './lista-problemas/ProblemasSection'
import { MedicamentosSection } from './medicamentos/MedicamentosSection'
import { MedicoesSection } from './medicoes/MedicoesSection'

interface AsideViewProps {
  prontuarioId: ID
  dataAtendimento: Instant
  cidadaoId: ID
  problemasAvaliados?: ProblemaCondicaoModel[]
  alergiasAvaliadas?: AlergiaReacaoModel[]
  lembretesCache?: LembreteFormModel[]
}

export const AsideView = (props: AsideViewProps) => {
  const { prontuarioId, dataAtendimento, cidadaoId, problemasAvaliados, alergiasAvaliadas, lembretesCache } = props
  const { scrollY } = useScrollPosition()
  const isCabecalhoMinimizado = useCabecalhoMinimizado()
  const { classes } = useLocalStyles(createStyles, isCabecalhoMinimizado, scrollY)

  const renderSections = useMemo(() => {
    return (
      <VFlow>
        <Grid>
          <Cell size={12}>
            <AlergiasSection prontuarioId={prontuarioId} alergiasAvaliadas={alergiasAvaliadas ?? []} />
            <ProblemasSection prontuarioId={prontuarioId} problemasAvaliados={problemasAvaliados ?? []} />
            <MedicoesSection prontuarioId={prontuarioId} dataAtendimento={dataAtendimento} />
            <MedicamentosSection prontuarioId={prontuarioId} dataAtendimento={dataAtendimento} />
            <CondicoesAutorreferidasSection cidadaoId={cidadaoId} />
            <CheckPermission permission={grupoCboLembretes.visualizar}>
              <LembretesSection prontuarioId={prontuarioId} lembretesCache={lembretesCache} />
            </CheckPermission>
          </Cell>
        </Grid>
      </VFlow>
    )
  }, [alergiasAvaliadas, cidadaoId, dataAtendimento, lembretesCache, problemasAvaliados, prontuarioId])

  return <aside className={classes.aside}>{renderSections}</aside>
}

const createStyles = (theme: Theme, isCabecalhoMinimizado: boolean, scrollY: number) => ({
  aside: {
    width: 'inherit',
    maxWidth: 'inherit',
    paddingRight: isCabecalhoMinimizado ? '1rem' : '1.5rem',
    borderRight: `1px solid ${theme.pallete.divider}`,
    position: isCabecalhoMinimizado ? 'fixed' : 'static',
    top: '8.5rem',
    height: `calc(100% - ${isCabecalhoMinimizado ? calcAsideHeightDiff(scrollY) + 'rem' : '0'})`,
    overflow: isCabecalhoMinimizado ? 'hidden auto' : 'hidden',
    [theme.breakpoints.up('xl')]: {
      //1920
      width: isCabecalhoMinimizado && 'calc(100% / 12 * 2.73)',
    },
    [theme.breakpoints.down('xl')]: {
      //1920
      width: isCabecalhoMinimizado && 'calc(100% / 12 * 3.18)',
    },
    [theme.breakpoints.down('md')]: {
      //960
      width: isCabecalhoMinimizado && 'calc(100% / 12 * 3.36)',
    },
    '::-webkit-scrollbar': {
      width: '0.3rem',
      height: '0.3rem',
    },
    '::-webkit-scrollbar-thumb': {
      background: theme.pallete.gray.c40,
      borderRadius: theme.radius.tag,
    },
    '::-webkit-scrollbar-thumb:hover': {
      background: theme.pallete.gray.c60,
    },
    '::-webkit-scrollbar-track': {
      background: theme.pallete.surface.main,
      borderRadius: theme.radius.tag,
      boxShadow: `inset 0.4rem 0.625rem 0.75rem ${theme.pallete.divider}`,
    },
  } as CSSProperties,
})

function calcAsideHeightDiff(scrollY: number): number {
  const distBottom = window.document.body.offsetHeight - (scrollY + window.innerHeight)
  return Math.max(0, 6.1 - distBottom / 16) + 9
}
